import { FC, ReactNode, useCallback, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { FormattedMessage } from 'react-intl'

import { Button } from '../Button'
import withTestId from '../hoc/withTestId'

const ButtonSave = withTestId(Button, 'button-save')
const ButtonDiscard = withTestId(Button, 'button-discard')

export type CropperModalProps = {
  isShow: boolean
  imageWidth?: number
  imageHeight?: number
  modalTitle?: ReactNode
  aspectRatio?: number
  imageSrc?: string
  onCancel: () => void
  onCrop: (blob: Blob) => void
}

const CropperModal: FC<CropperModalProps> = ({
  isShow,
  imageSrc,
  imageHeight,
  imageWidth,
  aspectRatio,
  modalTitle,
  onCancel,
  onCrop,
  ...props
}) => {
  const cropperRef = useRef<ReactCropperElement>(null)
  const [error, setError] = useState<string>('')

  const getBlob = useCallback(
    () =>
      new Promise<Blob>((resolve, reject) => {
        const cropper = cropperRef.current?.cropper

        if (!cropper) {
          reject(new Error('Cannot crop image'))
          return
        }

        const canvas = cropper.getCroppedCanvas({
          width: imageWidth,
          height: imageHeight,
          fillColor: '#fff',
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        })

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Cannot crop image'))
            return
          }

          resolve(blob)
        })
      }),
    [imageHeight, imageWidth]
  )

  const handleCrop = async () => {
    try {
      const blob = await getBlob()
      onCrop(blob)
    } catch (err) {
      setError(String(err))
    }
  }

  return (
    <Modal
      centered
      show={isShow}
      onHide={onCancel}
      size="lg"
      {...props}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h3">{modalTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <div className="alert alert-danger d-flex align-items-center p-5 mb-10">{error}</div>}

        <Cropper
          src={imageSrc}
          style={{ width: '100%', height: 480 }}
          aspectRatio={aspectRatio ?? 1 / 1}
          guides={true}
          ref={cropperRef}
          viewMode={1}
          cropBoxMovable={true}
          cropBoxResizable={true}
          background={true}
          checkCrossOrigin={true}
          checkOrientation={false}
          autoCropArea={1}
        />

        <div className="d-flex justify-content-center gap-5 mt-10 mb-5">
          <ButtonDiscard
            type="button"
            variant="light"
            onClick={onCancel}
          >
            <FormattedMessage id="form.discard" />
          </ButtonDiscard>
          <ButtonSave
            type="button"
            theme="primary"
            onClick={handleCrop}
          >
            <FormattedMessage id="form.save" />
          </ButtonSave>
        </div>
      </Modal.Body>
    </Modal>
  )
}

const CropperModalWithTestId = withTestId(CropperModal, 'cropper-modal')

export default CropperModalWithTestId
